class DesignerSpotlightItem extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = Array.from(this.querySelectorAll('.designer-spotlight__slide'));
    this.vendor = this.querySelector('[data-active-vendor]');
    this.productTitle = this.querySelector('[data-active-title]');
    this.link = this.querySelector('[data-active-link]');
    this.previousButton = this.querySelector('[data-carousel-previous]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    if (!this.track || this.slides.length === 0) return;

    this.onScroll = this.onScroll.bind(this);
    this.onScrollEnd = this.onScrollEnd.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onWindowBlur = this.onWindowBlur.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onDragStart = this.onDragStart.bind(this);
    this.onPrevious = this.onPrevious.bind(this);
    this.onNext = this.onNext.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.supportsNativeScrollEnd = 'onscrollend' in this.track;
    if (this.supportsNativeScrollEnd) {
      this.track.addEventListener('scrollend', this.onScrollEnd);
    }
    this.track.addEventListener('pointerdown', this.onPointerDown);
    this.track.addEventListener('pointermove', this.onPointerMove);
    this.track.addEventListener('pointerup', this.onPointerUp);
    this.track.addEventListener('pointercancel', this.onPointerUp);
    this.track.addEventListener('lostpointercapture', this.onPointerUp);
    window.addEventListener('blur', this.onWindowBlur);
    this.track.addEventListener('click', this.onClick, true);
    this.track.addEventListener('dragstart', this.onDragStart);
    this.previousButton?.addEventListener('click', this.onPrevious);
    this.nextButton?.addEventListener('click', this.onNext);

    if (this.slides.length > 1) {
      this.buildLoop();
    } else {
      this.carouselSlides = this.slides;
      this.updateActiveProduct();
    }

    this.resizeObserver = new ResizeObserver(() => {
      if (!this.track.clientWidth || this.track.classList.contains('is-dragging')) return;
      this.jumpToIndex(this.activeIndex ?? this.closestSlideIndex());
      this.updateActiveProduct();
    });
    this.resizeObserver.observe(this.track);
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.track?.removeEventListener('scrollend', this.onScrollEnd);
    this.track?.removeEventListener('pointerdown', this.onPointerDown);
    this.track?.removeEventListener('pointermove', this.onPointerMove);
    this.track?.removeEventListener('pointerup', this.onPointerUp);
    this.track?.removeEventListener('pointercancel', this.onPointerUp);
    this.track?.removeEventListener('lostpointercapture', this.onPointerUp);
    window.removeEventListener('blur', this.onWindowBlur);
    this.track?.removeEventListener('click', this.onClick, true);
    this.track?.removeEventListener('dragstart', this.onDragStart);
    this.previousButton?.removeEventListener('click', this.onPrevious);
    this.nextButton?.removeEventListener('click', this.onNext);
    this.resizeObserver?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.scrollEndTimer) clearTimeout(this.scrollEndTimer);
  }

  buildLoop() {
    const firstClone = this.createClone(this.slides[0]);
    const lastClone = this.createClone(this.slides[this.slides.length - 1]);

    this.track.prepend(lastClone);
    this.track.append(firstClone);
    this.carouselSlides = Array.from(this.track.querySelectorAll('.designer-spotlight__slide'));

    requestAnimationFrame(() => {
      this.jumpToIndex(1);
      this.updateActiveProduct();
    });
  }

  createClone(slide) {
    const clone = slide.cloneNode(true);
    clone.dataset.designerSlideClone = '';
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('tabindex', '-1');
    return clone;
  }

  slideWidth() {
    return this.carouselSlides[0].getBoundingClientRect().width;
  }

  centeredScrollLeft(index) {
    const slide = this.carouselSlides[index];
    if (!slide) return this.track.scrollLeft;

    const slideBounds = slide.getBoundingClientRect();
    const trackBounds = this.track.getBoundingClientRect();
    // Measure both centers in the same coordinate space, preserving fractional widths.
    return this.track.scrollLeft + slideBounds.left + slideBounds.width / 2 -
      (trackBounds.left + this.track.clientLeft + this.track.clientWidth / 2);
  }

  closestSlideIndex() {
    const trackCenter = this.track.getBoundingClientRect().left + this.track.clientLeft + this.track.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    this.carouselSlides.forEach((slide, index) => {
      const bounds = slide.getBoundingClientRect();
      const distance = Math.abs(bounds.left + bounds.width / 2 - trackCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  jumpToIndex(index) {
    const previousBehavior = this.track.style.scrollBehavior;
    this.track.style.scrollBehavior = 'auto';
    this.track.scrollLeft = this.centeredScrollLeft(index);
    this.track.style.scrollBehavior = previousBehavior;
  }

  onScroll() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.updateActiveProduct());

    // Releasing a drag at an existing snap point may not emit another scrollend.
    if (!this.supportsNativeScrollEnd || this.dragSettlePending) {
      if (this.scrollEndTimer) clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = setTimeout(this.onScrollEnd, 200);
    }
  }

  onScrollEnd() {
    if (this.slides.length <= 1 || this.track.classList.contains('is-dragging')) return;

    this.dragSettlePending = false;
    if (this.scrollEndTimer) clearTimeout(this.scrollEndTimer);

    const currentIndex = this.closestSlideIndex();
    if (currentIndex <= 0) {
      this.jumpToIndex(this.slides.length);
    } else if (currentIndex >= this.carouselSlides.length - 1) {
      this.jumpToIndex(1);
    } else {
      const centeredLeft = this.centeredScrollLeft(currentIndex);
      // Native snapping permits off-center stops when a slide is wider than the viewport.
      if (Math.abs(this.track.scrollLeft - centeredLeft) > 1) {
        this.track.scrollTo({ left: centeredLeft, behavior: 'smooth' });
      }
    }
    this.updateActiveProduct();
  }

  onPointerDown(event) {
    // Touch devices use native overflow scrolling for momentum and smooth snapping.
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    this.pointerId = event.pointerId;
    this.pointerStartX = event.clientX;
    this.pointerStartScroll = this.track.scrollLeft;
    this.dragDistance = 0;
  }

  onPointerMove(event) {
    if (event.pointerId !== this.pointerId) return;
    this.dragDistance = event.clientX - this.pointerStartX;

    if (Math.abs(this.dragDistance) > 5 && !this.track.hasPointerCapture(event.pointerId)) {
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture(event.pointerId);
    }

    if (this.track.hasPointerCapture(event.pointerId)) {
      this.track.scrollLeft = this.pointerStartScroll - this.dragDistance;
    }
  }

  onPointerUp(event) {
    if (this.pointerId == null || event.pointerId !== this.pointerId) return;
    const wasDragging = this.track.classList.contains('is-dragging');
    const targetIndex = wasDragging ? this.closestSlideIndex() : null;
    this.pointerId = null;
    if (this.track.hasPointerCapture(event.pointerId)) {
      this.track.releasePointerCapture(event.pointerId);
    }
    this.track.classList.remove('is-dragging');

    if (wasDragging) {
      // A drag can return to its starting position and must still suppress the click.
      this.dragDistance = Math.max(Math.abs(this.dragDistance), 6);
      this.dragSettlePending = true;
      this.track.scrollTo({ left: this.centeredScrollLeft(targetIndex), behavior: 'smooth' });
      this.onScroll();
    }
  }

  onWindowBlur() {
    this.onPointerUp({ pointerId: this.pointerId });
  }

  onClick(event) {
    if (Math.abs(this.dragDistance) > 5) {
      event.preventDefault();
      event.stopPropagation();
      this.dragDistance = 0;
    }
  }

  onDragStart(event) {
    event.preventDefault();
  }

  onPrevious() {
    this.scrollBySlide(-1);
  }

  onNext() {
    this.scrollBySlide(1);
  }

  scrollBySlide(direction) {
    const width = this.slideWidth();
    if (!width) return;

    const currentIndex = this.closestSlideIndex();
    const targetIndex = Math.max(0, Math.min(currentIndex + direction, this.carouselSlides.length - 1));
    this.track.scrollTo({ left: this.centeredScrollLeft(targetIndex), behavior: 'smooth' });
  }

  updateActiveProduct() {
    const slidesToMeasure = this.carouselSlides || this.slides;
    const width = this.slideWidth();
    if (!width) return;

    const activeIndex = this.closestSlideIndex();
    const activeSlide = slidesToMeasure[activeIndex];
    if (!activeSlide) return;
    this.activeIndex = activeIndex;

    if (this.vendor) this.vendor.textContent = activeSlide.dataset.vendor || '';
    if (this.productTitle) this.productTitle.textContent = activeSlide.dataset.title || '';
    if (this.productTitle && activeSlide.dataset.url) {
      this.productTitle.href = activeSlide.dataset.url;
      this.productTitle.removeAttribute('aria-disabled');
      this.productTitle.removeAttribute('role');
    }
    if (this.link && activeSlide.dataset.url) {
      this.link.href = activeSlide.dataset.url;
      this.link.removeAttribute('aria-disabled');
      this.link.removeAttribute('role');
    }
  }
}

if (!customElements.get('designer-spotlight-item')) {
  customElements.define('designer-spotlight-item', DesignerSpotlightItem);
}
